import React, { useRef, useState } from "react";
import { Image } from "antd";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";

const ICONS = "/iceholidays-assets/images/icons";
const FALLBACK_IMAGE = "/iceholidays-assets/images/china_listings_cover.png";
const SWIPE_THRESHOLD_PX = 40;

interface Props {
  images: string[];
}

// Cover image + "View All Photos (N)" pill + dot pagination + prev/next arrows and swipe,
// per Figma. Reuses antd Image.PreviewGroup for the lightbox (the mechanism
// components/shared/ItineraryImages.tsx also relies on) rather than that component's
// chrome, since the visual spec here (dark pill w/ count, pill+dot pagination) differs
// materially from the existing series show-page gallery.
export default function GroundTourGallery({ images }: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const slides = images.length > 0 ? images : [FALLBACK_IMAGE];
  const touchStartX = useRef<number | null>(null);

  const step = (delta: number) => {
    setActiveIndex((current) => (current + delta + slides.length) % slides.length);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;
    step(deltaX < 0 ? 1 : -1);
  };

  return (
    <div className="ground-tour-show-v2__gallery">
      {/* Blurred backdrop shows through the margins once the frame narrows on desktop. */}
      <div
        className="ground-tour-show-v2__gallery-backdrop"
        style={{ backgroundImage: `url(${slides[activeIndex]})` }}
        aria-hidden="true"
      />
      <div className="ground-tour-show-v2__gallery-scrim" aria-hidden="true" />

      <div className="ground-tour-show-v2__gallery-frame">
        <button
          type="button"
          className="ground-tour-show-v2__gallery-image"
          onClick={() => setPreviewVisible(true)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          aria-label="View photo gallery"
        >
          <img src={slides[activeIndex]} alt="" />
        </button>

        {slides.length > 1 && (
          <>
            <button
              type="button"
              className="ground-tour-show-v2__gallery-arrow ground-tour-show-v2__gallery-arrow--prev"
              onClick={() => step(-1)}
              aria-label="Previous photo"
            >
              <Icon path={mdiChevronLeft} size="20px" />
            </button>
            <button
              type="button"
              className="ground-tour-show-v2__gallery-arrow ground-tour-show-v2__gallery-arrow--next"
              onClick={() => step(1)}
              aria-label="Next photo"
            >
              <Icon path={mdiChevronRight} size="20px" />
            </button>
          </>
        )}

        {slides.length > 1 && (
          <button
            type="button"
            className="ground-tour-show-v2__gallery-viewall"
            onClick={() => setPreviewVisible(true)}
          >
            <img src={`${ICONS}/icon-view-all-photos.png`} alt="" aria-hidden="true" />
            View All Photos ({slides.length})
          </button>
        )}

        {slides.length > 1 && (
          <div className="ground-tour-show-v2__gallery-dots">
            {slides.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Photo ${index + 1}`}
                className={`ground-tour-show-v2__gallery-dot ${index === activeIndex ? "is-active" : ""}`}
                onClick={() => setActiveIndex(index)}
              />
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "none" }}>
        <Image.PreviewGroup
          items={slides}
          preview={{
            visible: previewVisible,
            current: activeIndex,
            onVisibleChange: (visible) => setPreviewVisible(visible),
            onChange: (current) => setActiveIndex(current),
          }}
        />
      </div>
    </div>
  );
}
